// Shared UI constants — named values for polling intervals, stale times,
// display limits, and feedback timers used across dashboard components.
//
// Caching architecture (two-tier freshness):
//
//   1. SSE PUSH (primary) — useLiveEvents opens a single SSE stream to
//      GET /api/events. The server emits a signal on every store mutation
//      (routines, calendar, contacts, projects, a conversation's active
//      project, ops errors, trash). The hook
//      translates each signal into a React Query invalidation → instant
//      re-render. On reconnect, gap detection replays missed signals or
//      invalidates everything.
//
//   2. POLLING (safety net / non-SSE stores) — refetchInterval on queries
//      whose data source does NOT emit SSE signals (email, analytics,
//      network, ollama, alerts). SSE-covered stores use a LONG
//      safety-net poll (2–5 min) purely as a fallback for prolonged SSE
//      outages — their primary freshness channel is the push.
//
// Best-practice alignment: TanStack Query docs + TkDodo's "Practical React
// Query" series recommend SSE/WebSocket invalidation as the primary channel
// with polling as a fallback, never both at the same cadence.

// ── SSE-covered stores: safety-net polling ──────────────────────────────────
// These stores emit liveEvents.publish() on every mutation. SSE handles the
// real-time invalidation; the poll is purely a fallback for extended outages.

/** Safety-net poll for SSE-covered stores (routines, calendar, projects, the
 *  active project and the file tree's root).
 *  SSE handles real-time invalidation; this is purely a fallback for
 *  extended SSE outages (e.g. relay disconnect > 30s backoff ceiling). */
export const SSE_SAFETY_NET_POLL_MS = 2 * 60 * 1000;

// ── Non-SSE stores: active polling ──────────────────────────────────────────
// These stores have no server-push channel — polling is the primary
// freshness mechanism. Intervals are tuned to the data's change frequency
// and the user's expectation of liveness.

/** Nav badge (email unread / alert count) — shared by TopNav and BottomTabBar. */
export const NAV_BADGE_POLL_MS = 60_000;

/** Email message list poll. */
export const EMAIL_LIST_POLL_MS = 30_000;

/** Analytics overview / costs — moderate refresh for dashboard stats. */
export const ANALYTICS_POLL_MS = 60_000;

/** Analytics activity feed — tighter for live-feel ops dashboard. */
export const ACTIVITY_FEED_POLL_MS = 15_000;

/** Alerts banner refresh. */
export const ALERTS_POLL_MS = 60_000;

/** Routine run history — changes only when a routine fires. */
export const ROUTINE_RUNS_POLL_MS = 30_000;

// ── Tight polls (user is actively waiting) ──────────────────────────────────

/** Update-check poll for web/relay clients (no electron-updater IPC). */
export const UPDATE_CHECK_POLL_MS = 30 * 60 * 1000;

/** Post-restart: initial delay before first health probe (process needs time to exit). */
export const RESTART_INITIAL_DELAY_MS = 3_000;

/** Post-restart: subsequent health probe interval. */
export const RESTART_POLL_MS = 2_000;

/** Connection-verification poll (Claude CLI link, GitHub auth callback). */
export const CONNECTION_VERIFY_POLL_MS = 3_000;

/** Ollama status poll — tight because the user is actively waiting for model pull / start. */
export const OLLAMA_STATUS_POLL_MS = 3_000;

/** Network panel status poll. */
export const NETWORK_STATUS_POLL_MS = 5_000;

/** External MCP panel poll while a server is connecting or retrying (none once all are connected). */
export const EXTERNAL_MCP_STATUS_POLL_MS = 5_000;

// ── Voice input ──────────────────────────────────────────────────────────────

/** How often the recording's level monitor samples the mic (its peak goes into the MicReport). */
export const MIC_LEVEL_POLL_MS = 200;

// ── Summarize job polling ────────────────────────────────────────────────────

/** Active poll while a summarize job is in-progress. */
export const SUMMARIZE_POLL_ACTIVE_MS = 1_000;

/** Retry/initial poll before the job is confirmed running. */
export const SUMMARIZE_POLL_RETRY_MS = 2_000;

// ── staleTime tiers ─────────────────────────────────────────────────────────
// staleTime controls "how long is cached data considered fresh before a
// background refetch on mount." With SSE invalidation, fresh data that
// changes server-side is invalidated instantly, so staleTime for SSE-covered
// stores can be long — it only affects unchanged data.

/** SSE-covered stores — high staleTime because SSE handles invalidation. */
export const STALE_SSE_COVERED_MS = 60_000;

/** Reference data that rarely changes within a session (model catalog, channel configs). */
export const STALE_REFERENCE_MS = Infinity;

/** Live dashboard data without SSE coverage — staleTime matches poll interval. */
export const STALE_LIVE_MS = 30_000;

// ── Display limits ───────────────────────────────────────────────────────────

/** Max recent conversations shown in the command palette. */
export const PALETTE_MAX_CONVERSATIONS = 10;

/** Max events rendered per day cell in the calendar grid. */
export const CALENDAR_MAX_EVENTS_PER_CELL = 3;

/** Max upcoming events shown below the calendar grid. */
export const CALENDAR_MAX_UPCOMING = 8;

/** Error message preview truncation in the activity feed. */
export const ACTIVITY_ERROR_PREVIEW_CHARS = 80;

/** Sub-agent / tool task label truncation. */
export const TASK_LABEL_MAX_CHARS = 120;

// ── Phone chat composer ──────────────────────────────────────────────────────

/** Smallest height (px) of the phone composer — the textarea plus its buttons. */
export const COMPOSER_MIN_PX = 100;

/** Largest share of the chat panel the phone composer may take, by drag or by a
 *  saved height from a taller screen (e.g. before rotating to landscape). */
export const COMPOSER_MAX_RATIO = 0.55;

// ── Chat streaming ───────────────────────────────────────────────────────────

/** Longest a streamed content update (text, tool call, sub-agent progress) waits
 *  before the chat re-renders — at most ~20 renders a second while a reply
 *  streams, however many events arrive. Each render then runs as its own task,
 *  so React's lower-priority work gets to run between them (see chatStore.internal.ts). */
export const STREAM_NOTIFY_INTERVAL_MS = 50;

// ── Feedback timers ──────────────────────────────────────────────────────────

/** Duration the "Copied" / "Saved" confirmation is shown before resetting. */
export const FEEDBACK_DISPLAY_MS = 1_500;
